import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FlexModule } from '@angular/flex-layout'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatTableModule } from '@angular/material/table'
import { errorMessage, Transaction, TransactionModel } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

import { EstimateBalanceApiService } from '../../services/estimate-balance-api.service'

@Component({
  selector: 'mmx-balance-transactions-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatProgressBarModule,
    MatTableModule,
    TranslateModule,
    FlexModule,
  ],
  templateUrl: './balance-transactions-card.component.html',
})
export class BalanceTransactionsCardComponent implements OnChanges {
  @Input() balanceId: string | null = null
  @Output() readonly refundRequested = new EventEmitter<TransactionModel>()

  readonly transactions = signal<TransactionModel[]>([])
  readonly loading = signal<boolean>(false)
  /** i18n key; the template pipes through `translate`. Never a raw backend string. */
  readonly error = signal<string | null>(null)
  readonly displayedColumns = ['createdAt', 'method', 'type', 'status', 'amount', 'actions']

  private readonly destroyRef = inject(DestroyRef)

  constructor(private readonly api: EstimateBalanceApiService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['balanceId']) {
      this.fetch()
    }
  }

  refresh(): void {
    this.fetch()
  }

  canRefund(transaction: TransactionModel): boolean {
    return transaction.status === Transaction.STATUS.SUCCESS
      && transaction.type === Transaction.TYPE.PAYMENT
  }

  onRefund(transaction: TransactionModel): void {
    if (!this.canRefund(transaction)) {
      return
    }
    this.refundRequested.emit(transaction)
  }

  private fetch(): void {
    if (!this.balanceId) {
      return
    }
    this.loading.set(true)
    this.error.set(null)
    this.api.listTransactions(this.balanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.transactions.set(rows)
          this.loading.set(false)
        },
        error: (err: unknown) => {
          this.loading.set(false)
          this.error.set(errorMessage(err, 'balance.transactions.error'))
        },
      })
  }
}
